import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const MarkupMargin = () => {
  // State variables for calculations
  const [cost, setCost] = useState(0); // Cost of the product
  const [sellingPrice, setSellingPrice] = useState(0); // Selling price of the product
  const [marginResult, setMarginResult] = useState(0); // Calculated margin
  const [markupResult, setMarkupResult] = useState(0); // Calculated markup

  // Event handlers for input changes
  const handleCostChange = (e) => setCost(parseFloat(e.target.value) || 0);
  const handleSellingPriceChange = (e) => setSellingPrice(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate margin and markup when inputs change
  useEffect(() => {
    if (cost > 0 && sellingPrice > 0) {
      const marginCalculation = ((sellingPrice - cost) / sellingPrice) * 100;
      const markupCalculation = ((sellingPrice - cost) / cost) * 100;
      setMarginResult(marginCalculation);
      setMarkupResult(markupCalculation);
    } else {
      setMarginResult(0);
      setMarkupResult(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [cost, sellingPrice]);

  return (
    <Layout title="Markup vs. Margin Calculator" path="markup-margin-calculator">
      <section className="section-padding" id="markup-margin-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Markup vs. Margin Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="cost" className="form-label">
                        Cost of Product ($)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Cost of Product:</strong> Enter the total cost of producing or acquiring the product."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="cost"
                        placeholder="Enter cost"
                        value={cost || ""}
                        onChange={handleCostChange}
                      />
                    </div>

                    {/* Selling Price Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="sellingPrice" className="form-label">
                        Selling Price ($)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Selling Price:</strong> Enter the price at which the product is sold."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="sellingPrice"
                        placeholder="Enter selling price"
                        value={sellingPrice || ""}
                        onChange={handleSellingPriceChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Margin:{" "}
                        <span className="fw-bold ms-3">
                          {marginResult === 0 ? "0.00" : marginResult.toFixed(2)}%
                        </span>
                      </h6>
                      <h6>
                        Markup:{" "}
                        <span className="fw-bold ms-3">
                          {markupResult === 0 ? "0.00" : markupResult.toFixed(2)}%
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Markup vs. Margin Calculator
                </h3>

                {/* What is the Markup vs. Margin Calculator? */}
                <h4 className="mt-3 mb-3">What is a Markup vs. Margin Calculator?</h4>
                <p>
                  The **Markup vs. Margin Calculator** is a tool that helps businesses differentiate between markup and margin for accurate pricing. Markup refers to the percentage increase over the cost price, while margin represents the profit percentage based on the selling price.
                </p>

                {/* Why Use a Markup vs. Margin Calculator? */}
                <h4 className="mt-4 mb-3">Why Use This Calculator?</h4>
                <p>
                  Understanding the difference between markup and margin ensures that a business can price its products strategically. Using this calculator helps maintain profit levels while staying competitive.
                </p>

                {/* Benefits of Using a Markup vs. Margin Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Markup vs. Margin Calculator</h4>
                <ul>
                  <li><strong>Ensures transparent pricing strategies:</strong> Businesses can see the full impact of pricing choices.</li>
                  <li><strong>Maintains profitability:</strong> Adjust prices with clarity to preserve profit margins.</li>
                  <li><strong>Adjusts for cost changes:</strong> Reassess and modify prices as costs fluctuate.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use This Calculator?</h4>
                <p>
                  This calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Retail businesses:</strong> Establish competitive and profitable pricing.</li>
                  <li><strong>Wholesalers:</strong> Ensure that bulk pricing aligns with profit goals.</li>
                  <li><strong>E-commerce platforms:</strong> Regularly adjust prices to reflect costs and maintain margin.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MarkupMargin;
